import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { publicRequest } from "../requestMethods";
import theme from "../theme";

const Widget = styled.div`
  margin: 0 10px;
  padding: 0 0 10px;
`;

const Label = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  padding: 0 4px;
  margin-bottom: 10px;
`;

const ChartWrap = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 10px;
  padding: 28px 10px 4px;
  margin-bottom: 10px;
  position: relative;
`;

const ChartBadge = styled.div`
  position: absolute;
  top: 8px; left: 8px;
  background: ${theme.bg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 700;
  color: ${theme.textMuted};
  z-index: 1;
`;

const StatsBox = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 10px;
  padding: 12px 12px 6px;
`;

const StatsTitle = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.textMain};
  margin-bottom: 10px;
`;

const StatRow = styled.div`margin-bottom: 10px;`;

const StatRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const StatRowLabel = styled.span`font-size: 11.5px; color: ${theme.textSub}; font-weight: 500;`;
const StatRowValue = styled.span`font-size: 12px; font-weight: 800; color: ${theme.textMain};`;

const StatBar = styled.div`
  height: 4px; border-radius: 2px; background: ${theme.bgAlt}; overflow: hidden;
`;

const StatBarFill = styled.div`
  height: 100%; border-radius: 2px; background: ${theme.primary};
  width: ${(p) => p.$pct}%; transition: width 0.6s ease;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-top: 1px solid ${theme.separator};
`;

const InlineLabel = styled.span`font-size: 11.5px; color: ${theme.textSub}; font-weight: 500;`;
const InlineValue = styled.span`font-size: 12px; font-weight: 700; color: ${theme.primary};`;

export default function ActivityWidget() {
  const currentUser = useSelector((s) => s.user.currentUser);
  const [myNotes, setMyNotes] = useState([]);
  const [followerCount, setFollowerCount] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    publicRequest.get(`/notes/profile/${currentUser._id}`)
      .then((res) => setMyNotes(res.data))
      .catch(() => {});
    // Fetch fresh user data so follower count is accurate
    publicRequest.get(`/users/${currentUser._id}`)
      .then((res) => setFollowerCount(res.data.followers?.length ?? 0))
      .catch(() => {});
  }, [currentUser]);

  if (!currentUser) return null;

  const totalLikes = myNotes.reduce((s, n) => s + (n.likes?.length || 0), 0);
  const ratedNotes = myNotes.filter((n) => n.ratings?.length > 0);
  const avgRating = ratedNotes.length
    ? (ratedNotes.reduce((s, n) => s + n.ratings.reduce((a, r) => a + r.value, 0) / n.ratings.length, 0) / ratedNotes.length).toFixed(1)
    : null;

  const sparkData = (() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { day: d.toLocaleDateString("en", { weekday: "short" }), count: 0 };
    });
    myNotes.forEach((n) => {
      const daysAgo = Math.floor((Date.now() - new Date(n.createdAt)) / 86400000);
      if (daysAgo < 7) days[6 - daysAgo].count += 1;
    });
    return days;
  })();

  return (
    <Widget>
      <Label>Activity</Label>
      <ChartWrap>
        <ChartBadge>{myNotes.length} notes</ChartBadge>
        <ResponsiveContainer width="100%" height={60}>
          <AreaChart data={sparkData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="sparkGradShared" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5aa7d4" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#5aa7d4" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="count" stroke="#5aa7d4" strokeWidth={2}
              fill="url(#sparkGradShared)" dot={false} isAnimationActive={true} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartWrap>
      <StatsBox>
        <StatsTitle>Quick Stats</StatsTitle>
        <StatRow>
          <StatRowTop>
            <StatRowLabel>Notes Shared</StatRowLabel>
            <StatRowValue>{myNotes.length}</StatRowValue>
          </StatRowTop>
          <StatBar><StatBarFill $pct={Math.min(myNotes.length * 10, 100)} /></StatBar>
        </StatRow>
        <InlineRow>
          <InlineLabel>Followers</InlineLabel>
          <InlineValue>{followerCount ?? currentUser.followers?.length ?? 0}</InlineValue>
        </InlineRow>
        <InlineRow>
          <InlineLabel>Total Likes</InlineLabel>
          <InlineValue>{totalLikes}</InlineValue>
        </InlineRow>
        {avgRating && (
          <InlineRow>
            <InlineLabel>Avg Rating</InlineLabel>
            <InlineValue>★ {avgRating}</InlineValue>
          </InlineRow>
        )}
      </StatsBox>
    </Widget>
  );
}
